import React, { useContext, useEffect, useState } from 'react';
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  Platform,
  SafeAreaView,
  ActivityIndicator,
  StatusBar,
  Animated,
} from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../navigation/stackTypes';
import { updateUserOnServer } from '../store/actions/user-actions';
import { RouteNameContext } from '../navigation/RouteNameContext';
import RoleService from '../services/role-service';
import { getPartnerTermsStatus } from '../services/partner-terms-service';
import { getPartnerChargers } from '../services/partner-service';
import PartnerInvitationNotification from './partner/PartnerInvitationNotification';
import { useMenu } from '../navigation/MenuContext';

const TEAL = '#376F78';
const GLOW_COLOR = '#FFFFFF';

const HostModeWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { openMenu } = useMenu();
  const primaryUse = useSelector(
    (state: RootState) => state.user.session?.primary_use
  );
  const userSession = useSelector((state: RootState) => state.user.session);
  const user = useSelector((state: RootState) => state.user.userObj);
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = React.useState(false);
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  
  // Get current route name from NavigationContext to avoid early access
  const currentRouteName = useContext(RouteNameContext);

  // Partner terms acceptance modal state
  const [showPartnerTermsModal, setShowPartnerTermsModal] = useState(false);
  const [hasCheckedPartnerTerms, setHasCheckedPartnerTerms] = useState(false);

  // Define screens where the host mode banner should be hidden
  const hideBannerScreens = [
    'OnboardingStatus',
    'SetupBilling', 
    'ChargerValidation'
  ];

  // Check if current screen should hide the banner
  const shouldHideBanner = !!currentRouteName && hideBannerScreens.includes(currentRouteName);
  // Glow animation
  const glowAnim = React.useRef(new Animated.Value(0)).current;

  React.useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(glowAnim, {
          toValue: 1,
          duration: 1200,
          useNativeDriver: false,
        }),
        Animated.timing(glowAnim, {
          toValue: 0,
          duration: 1200,
          useNativeDriver: false,
        }),
      ])
    ).start();
  }, [glowAnim]);

  // Check if user is a partner and needs to accept terms
  useEffect(() => {
    const checkPartnerTermsStatus = async () => {
      if (hasCheckedPartnerTerms || !userSession || RoleService.isGuest(userSession)) {
        return;
      }

      // Only check if user has partner role
      if (!RoleService.hasRole(userSession, 'partner')) {
        return;
      }

      try {
        // Check if user has pending partner tasks
        const hasPendingPartnerTask = user?.partner_tasks?.includes('ACCEPT_PARTNER_SPLIT_AGREEMENT');

        if (hasPendingPartnerTask) {
          // First, check if user actually has split chargers
          const chargersResponse = await getPartnerChargers();
          if (chargersResponse && chargersResponse.payload) {
            const chargers = (chargersResponse.payload as any)?.chargers || [];
            
            // Check if any charger has 'split' permission
            const hasSplitChargers = chargers.some((c: any) => 
              c.permissions?.includes('split')
            );
            
            // Only show modal if user has split chargers
            if (hasSplitChargers) {
              // Check if user has accepted terms
              const termsResponse = await getPartnerTermsStatus('SPLIT_PARTNER_TERMS');
              const termsAccepted = termsResponse.payload?.hasAgreement || false;

              if (!termsAccepted) {
                // Show modal - no need to fetch charger info as terms are account-wide
                setShowPartnerTermsModal(true);
              }
            }
          }
        }

        setHasCheckedPartnerTerms(true);
      } catch (error) {
        console.error('Error checking partner terms status:', error);
        setHasCheckedPartnerTerms(true);
      }
    };

    checkPartnerTermsStatus();
  }, [userSession, user?.partner_tasks, hasCheckedPartnerTerms]);

  const handleReturnToDriver = async () => {
    if (userSession) {
      setLoading(true);
      // Await server call before updating UI
      await dispatch(
        updateUserOnServer(userSession, { primary_use: 'driver' }, undefined)
      );
      setLoading(false);
      // Open the side menu instead of navigating to MyAccount screen
      openMenu();
      // If failed, error notification is handled in updateUserOnServer
    }
  };

  const handleReviewPartnerTerms = () => {
    setShowPartnerTermsModal(false);
    navigation.navigate('SplitPartnerships');
  };

  const handleDismissPartnerModal = () => {
    setShowPartnerTermsModal(false);
  };

  return (
    <View style={styles.container}>
      {/* Main content */}
      <View style={styles.content}>{children}</View>

      {/* Partner terms acceptance modal */}
      <PartnerInvitationNotification
        visible={showPartnerTermsModal}
        charger={null}
        onReviewTerms={handleReviewPartnerTerms}
        onDismiss={handleDismissPartnerModal}
      />

      {/* Host mode overlay with borders and status bar */}
      {primaryUse === 'host' && !shouldHideBanner && (
        <View style={styles.overlayContainer} pointerEvents="box-none">
          {/* Top banner extending from top of screen */}
          <View style={styles.bannerContainer}>
            <Pressable
              style={styles.banner}
              onPress={handleReturnToDriver}
              disabled={loading}
            >
              <Text style={styles.bannerText}>
                Host Mode, tap to return to Driver
              </Text>
              {loading ? (
                <ActivityIndicator
                  size="small"
                  color="#fff"
                  style={{ marginLeft: 8 }}
                />
              ) : null}
            </Pressable>
          </View>

          {/* Border frame with animated glow */}
          <View style={styles.borderFrame} pointerEvents="none">
            {/* Top border */}
            <Animated.View
              style={[
                styles.border,
                styles.topBorder,
                {
                  shadowColor: GLOW_COLOR,
                  shadowOffset: { width: 0, height: 0 },
                  shadowOpacity: glowAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0.7, 1],
                  }),
                  shadowRadius: glowAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [48, 128],
                  }),
                  backgroundColor: TEAL,
                },
              ]}
            />
            {/* Left border */}
            <Animated.View
              style={[
                styles.border,
                styles.leftBorder,
                {
                  shadowColor: GLOW_COLOR,
                  shadowOffset: { width: 0, height: 0 },
                  shadowOpacity: glowAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0.7, 1],
                  }),
                  shadowRadius: glowAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [48, 128],
                  }),
                  backgroundColor: TEAL,
                },
              ]}
            />
            {/* Right border */}
            <Animated.View
              style={[
                styles.border,
                styles.rightBorder,
                {
                  shadowColor: GLOW_COLOR,
                  shadowOffset: { width: 0, height: 0 },
                  shadowOpacity: glowAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0.7, 1],
                  }),
                  shadowRadius: glowAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [48, 1000],
                  }),
                  backgroundColor: TEAL,
                },
              ]}
            />
            {/* Bottom border */}
            <Animated.View
              style={[
                styles.border,
                styles.bottomBorder,
                {
                  shadowColor: GLOW_COLOR,
                  shadowOffset: { width: 0, height: 0 },
                  shadowOpacity: glowAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0.7, 1],
                  }),
                  shadowRadius: glowAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [48, 128],
                  }),
                  backgroundColor: TEAL,
                },
              ]}
            />
          </View>
        </View>
      )}

      {/* Status bar configuration */}
      <StatusBar
        backgroundColor={primaryUse === 'host' ? TEAL : 'transparent'}
        barStyle={primaryUse === 'host' ? 'light-content' : 'dark-content'}
        translucent={true}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
    backgroundColor: '#1A1D21',
  },
  content: {
    flex: 1,
    backgroundColor: '#1A1D21',
  },
  overlayContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
  },
  bannerContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: TEAL,
    zIndex: 1001, // Higher than other overlay elements
  },
  banner: {
    width: '100%',
    backgroundColor: TEAL,
    paddingTop: Platform.OS === 'ios' ? 51 : 28, // Increased padding to lower text position
    paddingBottom: 6,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 36,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  bannerText: {
    color: '#fff',
    fontSize: 13,
    letterSpacing: 0.2,
  },
  borderFrame: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
  },
  border: {
    position: 'absolute',
    backgroundColor: '#FFF',
  },
  topBorder: {
    top: 0,
    left: 0,
    right: 0,
    height: 2,
  },
  leftBorder: {
    top: 0,
    left: 0,
    bottom: 0,
    width: 2,
  },
  rightBorder: {
    top: 0,
    right: 0,
    bottom: 0,
    width: 2,
  },
  bottomBorder: {
    bottom: 0,
    left: 0,
    right: 0,
    height: 2,
  },
});

export default HostModeWrapper;
